import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { challengePaymentIntroPath, ScreenPath } from '../../navigation'
import { dataSource } from '../../service/domain/_service.config'
import { setActiveChallengeId } from '../challenge/activeChallenge'
import { useUser } from '../user/useUser'
import { useJoinTeam } from './useJoinTeam'
import { TEAM_QUERY_KEY, useTeam } from './useTeam'

export function useTeamJoinFlow(teamID: string) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: team } = useTeam(teamID)
  const challengeId = team?.challengeID ?? ''
  const { data: user, refetch: refetchUser } = useUser()
  const { mutate: joinTeamMutation, isPending: isJoiningTeam } = useJoinTeam()

  async function joinTeam() {
    if (!user || !challengeId) return

    const membership = await dataSource.getTeamMembershipForChallenge(user.id, challengeId)
    if (membership) {
      setActiveChallengeId(challengeId)
      navigate(`${ScreenPath.team}/${membership.teamID}`, { replace: true })
      return
    }
    if (isJoiningTeam) return

    const [freshUserResult, freshTeam] = await Promise.all([
      refetchUser(),
      queryClient.fetchQuery({
        queryKey: TEAM_QUERY_KEY(teamID),
        queryFn: () => dataSource.getTeam(teamID),
      }),
    ])

    const freshUser = freshUserResult.data
    if (!freshUser) return

    const userPaid = await dataSource.isUserEntitled(freshUser.id, challengeId)
    const teamPaid = freshTeam?.hasPaid === true

    if (!userPaid && !teamPaid) {
      navigate(
        `${challengePaymentIntroPath(challengeId)}?sourcePath=${encodeURIComponent(
          window.location.pathname,
        )}&action=${encodeURIComponent(
          JSON.stringify({
            type: 'JOIN_TEAM',
            payload: { teamId: teamID },
          }),
        )}`,
      )
    } else {
      joinTeamMutation(teamID, {
        onSuccess: () => {
          navigate(`${ScreenPath.team}/${teamID}`, { replace: true })
        },
      })
    }
  }

  return { joinTeam, isJoiningTeam }
}
